import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ToneOption, SizeOption } from '@/lib/hooks/use-ai-writing-assistant';

interface AIAssistantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (options: { customInstructions: string; tone?: ToneOption; size?: SizeOption; iterationPrompt?: string; previousGeneratedText?: string }) => Promise<string | null>;
  onAccept: (text: string) => void;
  generatedText: string | null;
  isLoading: boolean;
  type: 'bio' | 'client_bio' | 'project_description';
  currentText?: string;
}

const TONE_OPTIONS: ToneOption[] = ['professional', 'direct', 'friendly', 'casual', 'formal', 'conversational'];
const SIZE_OPTIONS: SizeOption[] = ['concise', 'moderate', 'elaborate', 'detailed'];

const TONE_LABELS: Record<ToneOption, string> = {
  professional: 'Professional',
  direct: 'Direct',
  friendly: 'Friendly',
  casual: 'Casual',
  formal: 'Formal',
  conversational: 'Conversational',
};

const SIZE_LABELS: Record<SizeOption, string> = {
  concise: 'Concise',
  moderate: 'Moderate',
  elaborate: 'Elaborate',
  detailed: 'Detailed',
};

export function AIAssistantDialog({ open, onOpenChange, onGenerate, onAccept, generatedText, isLoading, type }: AIAssistantDialogProps) {
  const [customInstructions, setCustomInstructions] = useState('');
  const [selectedTone, setSelectedTone] = useState<ToneOption>('professional');
  const [selectedSize, setSelectedSize] = useState<SizeOption>('moderate');
  const [iterationPrompt, setIterationPrompt] = useState('');
  const showPreview = !!generatedText;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setIterationPrompt('');
      setCustomInstructions('');
      setSelectedTone('professional');
      setSelectedSize('moderate');
    }
  }, [open]);

  const handleInitialGenerate = async () => {
    if (!customInstructions.trim()) {
      return; // Instructions are mandatory
    }
    
    await onGenerate({
      customInstructions: customInstructions.trim(),
      tone: selectedTone,
      size: selectedSize,
    });
    
    // The dialog will show preview when generatedText prop updates
  };

  const handleIterate = async () => {
    if (!iterationPrompt.trim() || !generatedText) {
      return;
    }
    
    await onGenerate({
      customInstructions: customInstructions.trim(),
      tone: selectedTone,
      size: selectedSize,
      iterationPrompt: iterationPrompt.trim(),
      previousGeneratedText: generatedText,
    });
    
    setIterationPrompt('');
  };

  const handleAccept = () => {
    if (generatedText) {
      onAccept(generatedText);
      // Reset all state before closing
      setIterationPrompt('');
      setCustomInstructions('');
      setSelectedTone('professional');
      setSelectedSize('moderate');
      onOpenChange(false);
    }
  };

  const handleDiscard = () => {
    setIterationPrompt('');
    setCustomInstructions('');
    setSelectedTone('professional');
    setSelectedSize('moderate');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#4669ed]" />
            AI Writing Assistant
          </DialogTitle>
          <DialogDescription>
            {showPreview 
              ? 'Review the generated content and iterate if needed'
              : `Customize how your ${type === 'client_bio' ? 'company bio' : type === 'project_description' ? 'project description' : 'bio'} is generated`
            }
          </DialogDescription>
        </DialogHeader>

        {!showPreview ? (
          // Initial generation form
          <div className="space-y-6 py-4">
            {/* Tone Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-slate-700">Tone</Label>
              <div className="flex flex-wrap gap-2">
                {TONE_OPTIONS.map((tone) => (
                  <Badge
                    key={tone}
                    variant={selectedTone === tone ? 'default' : 'outline'}
                    className={cn(
                      "cursor-pointer transition-colors rounded-full px-3 py-1.5 text-sm",
                      selectedTone === tone
                        ? 'bg-[#4669ed] text-white border-transparent hover:bg-[#2b42a3]'
                        : 'hover:bg-slate-100 border-slate-200'
                    )}
                    onClick={() => setSelectedTone(tone)}
                  >
                    {TONE_LABELS[tone]}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Size/Length Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-slate-700">Length</Label>
              <div className="flex flex-wrap gap-2">
                {SIZE_OPTIONS.map((size) => (
                  <Badge
                    key={size}
                    variant={selectedSize === size ? 'default' : 'outline'}
                    className={cn(
                      "cursor-pointer transition-colors rounded-full px-3 py-1.5 text-sm",
                      selectedSize === size
                        ? 'bg-[#4669ed] text-white border-transparent hover:bg-[#2b42a3]'
                        : 'hover:bg-slate-100 border-slate-200'
                    )}
                    onClick={() => setSelectedSize(size)}
                  >
                    {SIZE_LABELS[size]}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Custom Instructions - Mandatory */}
            <div className="space-y-2">
              <Label htmlFor="instructions" className="text-sm font-semibold text-slate-700">
                Instructions <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="instructions"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder="e.g., Emphasize my experience with machine learning, mention my startup background, highlight my expertise in NLP..."
                className="min-h-[100px] resize-none"
                maxLength={500}
                required
              />
              <p className="text-xs text-slate-400">{customInstructions.length}/500 characters</p>
            </div>
          </div>
        ) : (
          // Preview and iteration section
          <div className="space-y-6 py-4">
            {/* Generated Content Preview */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#4669ed]" />
                  Generated Content
                </Label>
              </div>
              <div className="rounded-xl border-2 border-[#4669ed]/30 bg-[#4669ed]/5 p-4">
                <div className="text-slate-700 leading-relaxed whitespace-pre-wrap bg-white rounded-lg p-4 border border-slate-200 max-h-[300px] overflow-y-auto">
                  {generatedText}
                </div>
              </div>
            </div>

            {/* Iteration Prompt */}
            <div className="space-y-2">
              <Label htmlFor="iteration" className="text-sm font-semibold text-slate-700">
                Refine the content (Optional)
              </Label>
              <Textarea
                id="iteration"
                value={iterationPrompt}
                onChange={(e) => setIterationPrompt(e.target.value)}
                placeholder="e.g., Make it more technical, add more details about my experience, make it shorter..."
                className="min-h-[80px] resize-none"
                maxLength={300}
              />
              <p className="text-xs text-slate-400">{iterationPrompt.length}/300 characters</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {!showPreview ? (
            // Initial generation buttons
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleInitialGenerate}
                disabled={isLoading || !customInstructions.trim()}
                className="bg-[#4669ed] hover:bg-[#2b42a3]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </>
          ) : (
            // Preview/iteration buttons
            <>
              <Button
                variant="outline"
                onClick={handleDiscard}
                disabled={isLoading}
              >
                <X className="h-4 w-4 mr-2" />
                Discard
              </Button>
              <Button
                variant="outline"
                onClick={handleIterate}
                disabled={isLoading || !iterationPrompt.trim()}
                className="border-[#4669ed] text-[#4669ed] hover:bg-[#4669ed]/10"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Regenerate
                  </>
                )}
              </Button>
              <Button
                onClick={handleAccept}
                disabled={isLoading}
                className="bg-[#4669ed] hover:bg-[#2b42a3]"
              >
                <Check className="h-4 w-4 mr-2" />
                Accept & Use
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

